import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { TranslationObject } from '@ngx-translate/core';
import { firstValueFrom, forkJoin, Observable } from 'rxjs';
import { AngularI18nService, Section } from 'common/data/i18n/services/angular-i18n.service';
import { I18N_MANIFEST } from 'common/data/i18n/registry/i18n-manifest.generated';
import { LanguageEnum, Language } from '@app/common/locale/domain/entities/language.enum';

@Injectable({ providedIn: 'root' })
export class I18nBootstrapService {
    private static readonly LIBRARY_LANGUAGES: ReadonlyArray<Language> = [
        LanguageEnum.ES,
        LanguageEnum.EN,
    ];
    private static readonly LIBRARY_ASSETS: ReadonlyArray<string> = [
        'ngx-jsonapi-material',
        'authfactor',
    ];
    private static readonly CRITICAL_SECTIONS: ReadonlyArray<Section> = [
        'common',
        'ui',
        'dialog-error',
        'header',
        'footer',
    ];

    public constructor(
        private readonly httpClient: HttpClient,
        private readonly angularI18nService: AngularI18nService,
        @Inject(PLATFORM_ID) private readonly platformId: object,
    ) {}

    public async loadLibraryTranslations(): Promise<void> {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        for (const language of I18nBootstrapService.LIBRARY_LANGUAGES) {
            try {
                const responses: Array<TranslationObject> = await Promise.all(
                    I18nBootstrapService.LIBRARY_ASSETS.map(
                        (asset): Promise<TranslationObject> =>
                            firstValueFrom(
                                this.httpClient.get<TranslationObject>(
                                    `/assets/i18n/${I18N_MANIFEST[`${asset}-${language}.json`] ?? `${asset}-${language}.json`}`,
                                ),
                            ),
                    ),
                );

                for (const response of responses) {
                    this.angularI18nService.mergeTranslations(language, response);
                }
            } catch {
                return;
            }
        }
    }

    public async initialize(): Promise<void> {
        await this.loadLibraryTranslations();
        await this.loadCriticalSections();
    }

    public async loadCriticalSections(): Promise<void> {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        await firstValueFrom(
            forkJoin(
                I18nBootstrapService.CRITICAL_SECTIONS.map(
                    (section): Observable<void> =>
                        this.angularI18nService.loadSectionAsync(section),
                ),
            ),
        );
    }
}
